import { useState, useEffect } from 'react';
import { tasksAPI, activitiesAPI, usersAPI } from '../utils/api';
import { useMasters } from '../hooks/useMasters';
import CustomFieldsEditor from './CustomFieldsEditor';

/**
 * frontend/src/components/BugFormFields.jsx
 *
 * The bug form, in one place.
 *
 * BugsPage and DevHubPage each had their own copy, which is how Dev Hub ended
 * up without the Related Task link while the Bugs page had it. Both now render
 * this, so a field added here appears in both.
 *
 * It renders FIELDS ONLY — no modal chrome, no save button. The two pages use
 * different modal shells and keep them.
 *
 * Every value below must exist on the bugs table or the route's allowlist:
 *   project_id task_id activity_id title issue_type severity status
 *   environment module section description steps_to_reproduce browser
 *   url_affected assigned_to
 */

// These are now FALLBACKS only — the live lists come from Masters. They are
// also the values the DB actually permits: the old BUG_ISSUE_TYPES offered
// 'Enhancement' and 'Question', neither of which bugs_issue_type_check allows,
// so choosing either was rejected on save.
export const BUG_SEVERITIES  = ['Minor', 'Major', 'Critical'];
export const BUG_ISSUE_TYPES = ['Bug', 'Request for Fix', 'Support', 'New Feature', 'REOPEN'];
export const BUG_ENVIRONMENTS = ['Development', 'UAT', 'Production'];
// Matches bugs_status_check — all ten values.
export const BUG_STATUSES = [
  'Open', 'In Progress', 'Fixed in Dev', 'In Testing', 'Fixed',
  'Deployed to UAT', 'Deployed to Production', 'Resolved', 'Verified', 'Closed',
];

/** Blank form. Spread this rather than writing the keys out again. */
export const emptyBugForm = () => ({
  custom_fields: {},
  project_id: '', task_id: '', activity_id: '', title: '', description: '',
  steps_to_reproduce: '', issue_type: 'Bug', severity: 'Minor', status: 'Open',
  environment: 'Development', module: '', section: '', browser: '',
  url_affected: '', assigned_to: '',
});

/** Load an existing bug into the form, dates and nulls normalised. */
export const bugToForm = (b) => ({
  ...emptyBugForm(),
  ...Object.fromEntries(Object.entries(emptyBugForm()).map(([k]) => [k, b?.[k] ?? ''])),
  issue_type:  b?.issue_type  || 'Bug',
  severity:    b?.severity    || 'Minor',
  status:      b?.status      || 'Open',
  environment: b?.environment || 'Development',
  // Last, so the generic key-mapping spread above cannot turn it into ''.
  custom_fields: b?.custom_fields || {},
});

const Field = ({ label, required, children, span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <label className="label">{label}{required && ' *'}</label>
    {children}
  </div>
);

export default function BugFormFields({ form, setForm, projects = [], users: usersProp, me, showStatus = false }) {
  // Admin-controlled lists, with the constants above as the fallback.
  const m = useMasters({
    severity:    ['bug_severity',    BUG_SEVERITIES],
    issueType:   ['bug_issue_type',  BUG_ISSUE_TYPES],
    environment: ['bug_environment', BUG_ENVIRONMENTS],
    status:      ['bug_status',      BUG_STATUSES],
  });

  const [loadedUsers, setLoadedUsers] = useState([]);
  const users = usersProp?.length ? usersProp : loadedUsers;

  // Self-heal: if the page passes an empty list (still loading, or its own
  // fetch failed), fetch here rather than showing an empty dropdown.
  useEffect(() => {
    if (usersProp?.length) return;
    let cancelled = false;
    usersAPI.getAll()
      .then(r => { if (!cancelled) setLoadedUsers(r.data || []); })
      .catch(() => { if (!cancelled) setLoadedUsers([]); });
    return () => { cancelled = true; };
  }, [usersProp]);

  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  // Tasks of the chosen project. Changing project clears task and activity so
  // a bug can never point at a task from a different project.
  useEffect(() => {
    if (!form.project_id) { setTasks([]); return; }
    let cancelled = false;
    tasksAPI.getAll({ project_id: form.project_id , limit: 0 })
      .then(r => { if (!cancelled) setTasks(r.data || []); })
      .catch(() => { if (!cancelled) setTasks([]); });
    return () => { cancelled = true; };
  }, [form.project_id]);

  useEffect(() => {
    if (!form.task_id) { setActivities([]); return; }
    let cancelled = false;
    activitiesAPI.getByTask(form.task_id)
      .then(r => { if (!cancelled) setActivities(r.data?.activities || r.data || []); })
      .catch(() => { if (!cancelled) setActivities([]); });
    return () => { cancelled = true; };
  }, [form.task_id]);

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Project" required span>
        <select className="select" value={form.project_id || ''}
          onChange={e => set({ project_id: e.target.value, task_id: '', activity_id: '' })}>
          <option value="">Select project…</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>

      {form.project_id && (
        <Field label="Related Task">
          <select className="select" value={form.task_id || ''}
            onChange={e => set({ task_id: e.target.value, activity_id: '' })}>
            <option value="">No specific task</option>
            {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </Field>
      )}

      {form.task_id && (
        <Field label="Related Activity">
          <select className="select" value={form.activity_id || ''}
            onChange={e => set({ activity_id: e.target.value })}>
            <option value="">No specific activity</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
        </Field>
      )}

      <Field label="Title" required span>
        <input className="input" value={form.title || ''}
          onChange={e => set({ title: e.target.value })} placeholder="Brief description" />
      </Field>

      <Field label="Issue Type">
        <select className="select" value={form.issue_type || 'Bug'}
          onChange={e => set({ issue_type: e.target.value })}>
          {m.issueType.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Severity">
        <select className="select" value={form.severity || 'Minor'}
          onChange={e => set({ severity: e.target.value })}>
          {m.severity.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>

      {showStatus && (
        <Field label="Status">
          <select className="select" value={form.status || 'Open'}
            onChange={e => set({ status: e.target.value })}>
            {m.status.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      )}
      <Field label="Environment">
        <select className="select" value={form.environment || 'Development'}
          onChange={e => set({ environment: e.target.value })}>
          {m.environment.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>

      <Field label="Module">
        <input className="input" value={form.module || ''}
          onChange={e => set({ module: e.target.value })} placeholder="e.g. Loans, Company" />
      </Field>
      <Field label="Section">
        <input className="input" value={form.section || ''}
          onChange={e => set({ section: e.target.value })} placeholder="e.g. Basic Info" />
      </Field>

      <Field label="Description" required span>
        <textarea className="input" rows={3} value={form.description || ''}
          onChange={e => set({ description: e.target.value })} placeholder="Explain the issue in detail…" />
      </Field>
      <Field label="Steps to Reproduce" span>
        <textarea className="input font-mono text-xs" rows={3} value={form.steps_to_reproduce || ''}
          onChange={e => set({ steps_to_reproduce: e.target.value })}
          placeholder={'1. Go to…\n2. Click…\n3. See error'} />
      </Field>

      <Field label="Browser">
        <input className="input" value={form.browser || ''}
          onChange={e => set({ browser: e.target.value })} placeholder="Chrome 120" />
      </Field>
      <Field label="URL Affected">
        <input className="input" value={form.url_affected || ''}
          onChange={e => set({ url_affected: e.target.value })} placeholder="https://…" />
      </Field>

      {/* Admin-defined fields. Renders nothing until one is configured, so
          the form is unchanged for anyone who has not set any up. */}
      <div className="col-span-2">
        <CustomFieldsEditor appliesTo="bug"
          values={form.custom_fields || {}}
          onChange={v => set({ custom_fields: v })} />
      </div>

      {/* An unassigned bug appears on nobody's list. */}
      <Field label="Assigned To" required span>
        <select className="select" value={form.assigned_to || ''}
          onChange={e => set({ assigned_to: e.target.value })}>
          <option value="">Select a person…</option>
          {users.filter(u => u.is_active).map(u => (
            <option key={u.id} value={u.id}>{u.name}{u.id === me?.id ? ' (me)' : ''}</option>
          ))}
        </select>
      </Field>
    </div>
  );
}

/** True when the form has everything the API requires. */
export const bugFormValid = (f) =>
  !!(f?.project_id && f?.title?.trim() && f?.description?.trim() && f?.assigned_to);
